import { STAYS_WISHLIST } from "@capone/experiments";
import { Button } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import clsx from "clsx";
import {
  ActionButton,
  DesktopPopupModal,
  getImagesArray,
  HotelName,
  HotelPriceAndRewardsDisplay,
  HotelStarRating,
  HotelTotalPanel,
  Icon,
  IconName,
  PhotoLayout,
} from "halifax";
import React from "react";
import { RouteComponentProps } from "react-router";
import { BaseAddress, LodgingCollectionEnum, StayTypesEnum } from "redmond";
import { StayType } from "redmond/hotels-module/interfaces";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import {
  AVAILABLE,
  getExperimentVariant,
  useExperiments,
} from "../../../../context/experiments";
import { WishlistToggleHeart } from "../../../wishlist";
import { goToVacationRentalsCheckout } from "../../utils/queryStringHelpers";
import { DesktopHotelPhotoModalConnectorProps } from "./container";
import "./styles.scss";

export interface DesktopHotelPhotoModalProps
  extends DesktopHotelPhotoModalConnectorProps,
    RouteComponentProps {
  isSkeleton?: boolean;
  handleReadyToRedirect?: () => void;
}

export const DesktopHotelPhotoModal = (props: DesktopHotelPhotoModalProps) => {
  const {
    history,
    roomInfoProducts,
    lodging,
    roomProduct,
    nightCount,
    isSkeleton,
    rewardsKey,
    viewedPremierCollectionDetailsProperties,
    stayType,
    listing,
    shopDetailsLodgingData,
  } = props;
  const [imagesArray, setImagesArray] = React.useState<string[]>([]);
  const [openModal, setOpenModal] = React.useState<boolean>(false);

  const expState = useExperiments();
  const isWishlistExperiment =
    getExperimentVariant(expState.experiments, STAYS_WISHLIST) === AVAILABLE;

  React.useEffect(() => {
    getImagesArray(
      shopDetailsLodgingData
        ? shopDetailsLodgingData.media
        : (stayType === StayTypesEnum.VacationRentals
            ? listing?.listing?.content?.media
            : lodging?.lodging.media) ?? [],
      setImagesArray
    );
  }, [lodging, setImagesArray, stayType, listing, shopDetailsLodgingData]);

  const filteredImagesArray = React.useMemo(
    () =>
      // The filter method is used to remove empty items in imagesArray
      imagesArray.filter((imageTuple) => !!imageTuple),
    [imagesArray]
  );

  return (
    <div className="desktop-pc-photo-modal">
      {isSkeleton && (
        <Skeleton className="desktop-pc-photo-modal-main-image-container skeleton" />
      )}
      {!isSkeleton && (
        <div
          className="desktop-pc-photo-modal-main-image-container"
          onClick={() => {
            {
              stayType === StayTypesEnum.Hotels &&
                trackEvent({
                  eventName: "hotel_shop_image_modal",
                  properties: {
                    ...viewedPremierCollectionDetailsProperties,
                    is_pc:
                      lodging?.lodgingCollection ===
                        LodgingCollectionEnum.Premier ||
                      shopDetailsLodgingData?.lodgingCollection ===
                        LodgingCollectionEnum.Premier,
                    is_lc:
                      lodging?.lodgingCollection ===
                        LodgingCollectionEnum.Lifestyle ||
                      shopDetailsLodgingData?.lodgingCollection ===
                        LodgingCollectionEnum.Lifestyle,
                  },
                });
            }
            setOpenModal(!openModal);
          }}
        >
          <div className="desktop-pc-photo-modal-main-image-section-left">
            <img
              className="main-image-left"
              src={filteredImagesArray[0]}
              alt="pc-1"
            />
          </div>
          <div className="desktop-pc-photo-modal-main-image-section-right">
            <div className="main-image-right top-image">
              <img src={filteredImagesArray[1]} alt="pc-2" />
            </div>
            <div className="main-image-right bottom-image">
              <img src={filteredImagesArray[2]} alt="pc-3" />
            </div>
          </div>
          <Button
            className="show-all-photos-button"
            onClick={() => {
              setOpenModal(true);
            }}
          >
            <Icon name={IconName.ShowAllImagesIcon} />
            <span className="show-all-photos-button-label">
              See all photos ({filteredImagesArray.length})
            </span>
          </Button>
          {stayType === StayTypesEnum.VacationRentals &&
            isWishlistExperiment &&
            listing &&
            listing.listingId.id &&
            listing.listing.listingCollection && (
              <WishlistToggleHeart
                listingId={listing.listingId.id}
                stayType={StayType.Homes}
                collection={listing.listing.listingCollection}
                listingImageUrl={listing.listing.content.media[0]?.url}
                style={{
                  position: "absolute",
                  top: 32 + 10,
                  right: 10,
                }}
              />
            )}
        </div>
      )}

      {/*TODO handle lodging for dateless search and modal button when click on images. need to change BE response*/}
      <DesktopPopupModal
        className={clsx("desktop-pc-photo-modal-popup", "b2b")}
        open={openModal}
        invisibleBackdrop={false}
        onClose={(event: React.MouseEvent) => {
          event.stopPropagation();
          setOpenModal(false);
        }}
      >
        <div
          id="desktop-pc-photo-modal-container"
          className="desktop-pc-photo-modal-container"
        >
          <div id="photo-gallery" className="photo-gallery">
            <PhotoLayout
              imgUrls={filteredImagesArray}
              scrollableTarget="photo-gallery"
            />
          </div>
          <div className="photo-gallery-pc-info">
            <HotelStarRating
              starRating={
                lodging?.lodging?.starRating ||
                shopDetailsLodgingData?.starRating
              }
              city={
                lodging
                  ? lodging?.lodging?.city
                  : (listing?.listing?.content?.location.address as BaseAddress)
                      ?.city ||
                    (shopDetailsLodgingData?.location as BaseAddress)?.city
              }
              isSkeleton={isSkeleton}
            />
            <HotelName
              name={
                lodging
                  ? lodging?.lodging?.name
                  : listing?.listing?.content?.name ||
                    shopDetailsLodgingData?.name
              }
              isSkeleton={isSkeleton}
            />
            {!!roomProduct && !!lodging && (
              <HotelTotalPanel
                chosenRate={roomProduct}
                selectedLodging={lodging}
                cheapestProduct={roomInfoProducts[0]?.products[0]}
                nightCount={nightCount || undefined}
                rewardsKey={rewardsKey}
                useRedesign
              />
            )}
            {!!listing && (
              <HotelPriceAndRewardsDisplay
                lodgingPrice={listing?.availability.rate?.price}
                nightCount={nightCount ?? 0}
                isSkeleton={isSkeleton}
                rewardsKey={rewardsKey}
                variant="default"
              />
            )}
            <ActionButton
              className="desktop-photo-modal-choose-room-cta"
              onClick={() => {
                setOpenModal(false);
                switch (stayType) {
                  case StayTypesEnum.VacationRentals:
                    goToVacationRentalsCheckout({ history });
                    break;
                  case StayTypesEnum.Hotels:
                  default:
                    trackEvent({
                      eventName: "select_room_from_main_image",
                      properties: {
                        ...viewedPremierCollectionDetailsProperties,
                        is_pc:
                          lodging?.lodgingCollection ===
                            LodgingCollectionEnum.Premier ||
                          shopDetailsLodgingData?.lodgingCollection ===
                            LodgingCollectionEnum.Premier,
                        is_lc:
                          lodging?.lodgingCollection ===
                            LodgingCollectionEnum.Lifestyle ||
                          shopDetailsLodgingData?.lodgingCollection ===
                            LodgingCollectionEnum.Lifestyle,
                      },
                    });
                    setTimeout(() => {
                      // For /shop page
                      document
                        .getElementsByClassName("reserve-cta")[0]
                        ?.scrollIntoView({ behavior: "smooth", block: "end" });
                      // For /shop-details page
                      document
                        .getElementsByClassName("cta-select-dates-text")[0]
                        ?.scrollIntoView({ behavior: "smooth", block: "end" });
                    }, 300);
                    break;
                }
              }}
              message={
                <span className="button-text">
                  {stayType === StayTypesEnum.Hotels
                    ? "Choose Room"
                    : "Reserve Now"}
                </span>
              }
            />
          </div>
        </div>
      </DesktopPopupModal>
    </div>
  );
};
